import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * The one feedback channel for state changes (spec §6: "5-second Undo toasts,
 * not confirm dialogs"). One toast at a time, with a hairline that drains over
 * the undo window. Two kinds of undo:
 *
 * - `defer`: nothing is sent yet. The page applies the change optimistically,
 *   the request runs when the window closes, and Undo simply cancels it. Used
 *   for anything the API cannot reverse cleanly (complete, decide, triage…).
 *   Commits pass `KEEPALIVE` (lifeos/api.ts) so one flushed on unload lands.
 * - `undoable`: the request already ran (it had to, e.g. to get the server's
 *   summary); Undo runs a compensating request.
 *
 * A pending deferred change is never lost: a newer toast, the page going into
 * the background, or the provider unmounting commits it immediately, and
 * `flush()` lets a caller wait for it (sign-out does, before the session ends).
 * The one exception is a change to the same setting (same `key`): it replaces
 * the pending one unsent, so Undo after 1→2→3 sends nothing at all.
 */

export const UNDO_MS = 5000;

export interface DeferredChange {
  /** What happened, in past tense: "Done #41 Call mom". */
  text: string;
  /** The request; runs once, when the window closes (or on flush). */
  commit: () => Promise<unknown>;
  /** Restore the optimistic UI after Undo. */
  onUndo?: () => void;
  /** After a successful commit (usually a reload). */
  onDone?: () => void;
  /** After a failed commit: restore the UI. The provider shows the error. */
  onError?: () => void;
  /** Error prefix, e.g. "Couldn't complete #41". */
  failText?: string;
  /**
   * Identifies the setting this change writes (e.g. `ceiling:<projectId>`).
   * A newer change with the same key replaces this one unsent and inherits
   * its `onUndo`, so Undo returns to the value the server still holds.
   */
  key?: string;
}

export interface UndoableChange {
  text: string;
  undo: () => Promise<unknown> | void;
  /** After a successful undo (usually a reload). */
  onUndone?: () => void;
}

export interface ToastApi {
  defer(change: DeferredChange): void;
  undoable(change: UndoableChange): void;
  info(text: string): void;
  error(text: string): void;
  /** Close the current toast, sending a pending deferred change now; resolves once it settled. */
  flush(): Promise<void>;
}

interface View {
  id: number;
  text: string;
  tone: 'info' | 'error';
  canUndo: boolean;
}

interface Live {
  id: number;
  timer: ReturnType<typeof setTimeout>;
  deferred?: DeferredChange;
  undoable?: UndoableChange;
}

const ToastContext = createContext<ToastApi | null>(null);

export function errorText(err: unknown): string {
  return err instanceof Error && err.message ? err.message : 'something went wrong';
}

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [view, setView] = useState<View | null>(null);
  const live = useRef<Live | null>(null);
  const seq = useRef(0);

  const engine = useMemo(() => {
    function close(): Live | null {
      const cur = live.current;
      if (!cur) return null;
      clearTimeout(cur.timer);
      live.current = null;
      setView(null);
      return cur;
    }

    /** Run the request now (synchronously, so a flush during page unload still starts it). Never rejects. */
    function commit(change: DeferredChange): Promise<void> {
      let sent: Promise<unknown>;
      try {
        sent = change.commit();
      } catch (err) {
        sent = Promise.reject(err);
      }
      return sent.then(
        () => change.onDone?.(),
        (err: unknown) => {
          change.onError?.();
          show({ text: `${change.failText ?? "That didn't save"}: ${errorText(err)}`, tone: 'error', canUndo: false }, {});
        },
      );
    }

    /** Close the current toast; a pending deferred change is committed now. */
    function flush(): Promise<void> {
      const cur = close();
      return cur?.deferred ? commit(cur.deferred) : Promise.resolve();
    }

    function defer(change: DeferredChange): void {
      const prev = live.current?.deferred;
      if (change.key !== undefined && prev?.key === change.key) {
        // Same setting again inside its window: drop the pending value unsent.
        close();
        change = { ...change, onUndo: prev.onUndo ?? change.onUndo };
      }
      show({ text: change.text, tone: 'info', canUndo: true }, { deferred: change });
    }

    function show(v: Omit<View, 'id'>, extra: Pick<Live, 'deferred' | 'undoable'>): void {
      void flush();
      const id = ++seq.current;
      const timer = setTimeout(() => {
        if (live.current?.id === id) void flush();
      }, UNDO_MS);
      live.current = { id, timer, ...extra };
      setView({ id, ...v });
    }

    function undo(): void {
      const cur = close();
      if (cur?.deferred) cur.deferred.onUndo?.();
      else if (cur?.undoable) {
        const u = cur.undoable;
        Promise.resolve()
          .then(u.undo)
          .then(
            () => u.onUndone?.(),
            (err: unknown) => show({ text: `Couldn't undo: ${errorText(err)}`, tone: 'error', canUndo: false }, {}),
          );
      }
    }

    const api: ToastApi = {
      defer,
      undoable: (change) => show({ text: change.text, tone: 'info', canUndo: true }, { undoable: change }),
      info: (text) => show({ text, tone: 'info', canUndo: false }, {}),
      error: (text) => show({ text, tone: 'error', canUndo: false }, {}),
      flush,
    };
    return { api, undo, flush };
  }, []);

  useEffect(() => {
    // Backgrounding the app (or closing it) must not drop a change in its window.
    const onHide = (): void => {
      if (document.visibilityState === 'hidden') void engine.flush();
    };
    const onPageHide = (): void => void engine.flush();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
      void engine.flush();
    };
  }, [engine]);

  return (
    <ToastContext.Provider value={engine.api}>
      {children}
      <div className="lo-toast-slot" role="status" aria-live="polite">
        {view && (
          <div key={view.id} className={`lo-toast lo-toast--${view.tone}`}>
            <span className="lo-toast__text">{view.text}</span>
            {view.canUndo && (
              <button type="button" className="lo-toast__undo" onClick={engine.undo}>
                Undo
              </button>
            )}
            <span className="lo-toast__bar" style={{ animationDuration: `${UNDO_MS}ms` }} aria-hidden="true" />
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast() needs a <ToastProvider> (Layout provides one)');
  return api;
}
